// src/components/workspace/investigation/FindingsPanel.js

import { useMemo, useState } from "react";
import {
  Check,
  CheckCircle2,
  Crosshair,
  Eye,
  Lightbulb,
  Paperclip,
  Trash2,
  Wrench,
  X,
} from "lucide-react";

import {
  FINDING_KIND,
  FINDING_STATUS,
  FINDING_STATUS_LABELS,
  authorableKinds,
  extractResolution,
  getFindingKind,
} from "../../../constants/findings";
import { formatClockTime } from "../../../utils/duration";

const ICONS = {
  Lightbulb,
  Wrench,
  Eye,
  Crosshair,
  CheckCircle2,
};

/**
 * The investigation record: what we think, what we did, what we saw, and what
 * turned out to be true.
 *
 * This is the expert's primary work surface. The customer sees the same record
 * read-only, which is what makes the €249 feel like engineering rather than a
 * chat window — they can watch the reasoning accumulate.
 */

function EvidenceChips({ evidenceIds, filesById }) {
  const resolved = (evidenceIds || [])
    .map((id) => filesById.get(id))
    .filter(Boolean);

  if (resolved.length === 0) return null;

  return (
    <div className="mt-1.5 flex flex-wrap gap-1">
      {resolved.map((file) => (
        <a
          key={file.id}
          href={file.downloadURL}
          target="_blank"
          rel="noreferrer"
          className="inline-flex max-w-full items-center gap-1 rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-2xs text-slate-600 transition hover:border-blue-300 hover:text-blue-700"
        >
          <Paperclip className="h-3 w-3 flex-shrink-0" />
          <span className="truncate">{file.filename}</span>
        </a>
      ))}
    </div>
  );
}

function ResolutionSummary({ rootCause, fix }) {
  if (!rootCause && !fix) return null;

  const rows = [
    rootCause && { kind: FINDING_KIND.ROOT_CAUSE, finding: rootCause },
    fix && { kind: FINDING_KIND.FIX, finding: fix },
  ].filter(Boolean);

  return (
    <div className="mb-4 space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
      <p className="text-2xs font-semibold uppercase tracking-wide text-slate-500">
        Resolution
      </p>
      {rows.map(({ kind, finding }) => {
        const definition = getFindingKind(kind);
        const Icon = ICONS[definition.icon] || Eye;
        return (
          <div key={kind} className="flex items-start gap-2">
            <Icon
              className={`mt-0.5 h-4 w-4 flex-shrink-0 ${definition.accentClass}`}
            />
            <div className="min-w-0">
              <p className="text-2xs font-semibold uppercase tracking-wide text-slate-400">
                {definition.label}
              </p>
              <p className="text-sm text-slate-800">{finding.text}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function FindingRow({
  finding,
  filesById,
  canManage,
  onSetStatus,
  onDelete,
}) {
  const definition = getFindingKind(finding.kind);
  const Icon = ICONS[definition.icon] || Eye;
  const isHypothesis = definition.verdictable;
  const ruledOut = finding.status === FINDING_STATUS.RULED_OUT;

  return (
    <li className="group flex items-start gap-2.5 rounded-lg border border-slate-200 px-3 py-2.5 transition hover:border-slate-300">
      <Icon
        className={`mt-0.5 h-4 w-4 flex-shrink-0 ${definition.accentClass}`}
      />

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span
            className={`rounded border px-1.5 py-0.5 text-2xs font-semibold uppercase tracking-wide ${definition.chipClass}`}
          >
            {definition.shortLabel}
          </span>
          {isHypothesis && finding.status !== FINDING_STATUS.OPEN && (
            <span className="text-2xs font-semibold uppercase tracking-wide text-slate-500">
              {FINDING_STATUS_LABELS[finding.status]}
            </span>
          )}
        </div>

        <p
          className={`mt-1 whitespace-pre-wrap text-sm ${
            ruledOut ? "text-slate-400 line-through" : "text-slate-800"
          }`}
        >
          {finding.text}
        </p>

        <EvidenceChips
          evidenceIds={finding.evidenceIds}
          filesById={filesById}
        />

        <p className="mt-1 text-2xs text-slate-400">
          {finding.authorName || "Unknown"}
          {finding.createdAt ? ` · ${formatClockTime(finding.createdAt)}` : ""}
        </p>
      </div>

      {canManage && (
        <div className="flex flex-shrink-0 items-center gap-1 opacity-0 transition focus-within:opacity-100 group-hover:opacity-100">
          {isHypothesis && finding.status !== FINDING_STATUS.CONFIRMED && (
            <button
              type="button"
              onClick={() => onSetStatus(finding, FINDING_STATUS.CONFIRMED)}
              className="rounded p-1 text-slate-400 transition hover:bg-emerald-50 hover:text-emerald-600"
              aria-label={`Confirm hypothesis: ${finding.text}`}
              title="Confirm"
            >
              <Check className="h-3.5 w-3.5" />
            </button>
          )}
          {isHypothesis && finding.status !== FINDING_STATUS.RULED_OUT && (
            <button
              type="button"
              onClick={() => onSetStatus(finding, FINDING_STATUS.RULED_OUT)}
              className="rounded p-1 text-slate-400 transition hover:bg-amber-50 hover:text-amber-600"
              aria-label={`Rule out hypothesis: ${finding.text}`}
              title="Rule out"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            type="button"
            onClick={() => onDelete(finding.id)}
            className="rounded p-1 text-slate-300 transition hover:bg-rose-50 hover:text-rose-600"
            aria-label={`Delete entry: ${finding.text}`}
            title="Delete"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </li>
  );
}

export default function FindingsPanel({
  findings,
  files,
  viewerRole,
  nextAction,
  nextActionOwner,
  onAddFinding,
  onSetFindingStatus,
  onDeleteFinding,
  onSaveNextAction,
  error,
  onRetry,
}) {
  const kinds = authorableKinds(viewerRole);
  const [kind, setKind] = useState(kinds[0]);
  const [text, setText] = useState("");
  const [selectedEvidence, setSelectedEvidence] = useState([]);
  const [saving, setSaving] = useState(false);

  const [editingNext, setEditingNext] = useState(false);
  const [nextDraft, setNextDraft] = useState(nextAction || "");
  const [ownerDraft, setOwnerDraft] = useState(nextActionOwner || "expert");

  const isExpert = viewerRole === "expert";

  const filesById = useMemo(
    () => new Map((files || []).map((f) => [f.id, f])),
    [files]
  );

  const { rootCause, fix } = useMemo(
    () => extractResolution(findings),
    [findings]
  );

  const handleAdd = async () => {
    const body = text.trim();
    if (!body || saving) return;

    setSaving(true);
    const result = await onAddFinding({
      kind,
      text: body,
      evidenceIds: selectedEvidence,
    });
    setSaving(false);

    if (result?.success !== false) {
      setText("");
      setSelectedEvidence([]);
    }
  };

  const toggleEvidence = (fileId) => {
    setSelectedEvidence((prev) =>
      prev.includes(fileId)
        ? prev.filter((id) => id !== fileId)
        : [...prev, fileId]
    );
  };

  const saveNext = async () => {
    await onSaveNextAction({
      nextAction: nextDraft.trim(),
      nextActionOwner: nextDraft.trim() ? ownerDraft : null,
    });
    setEditingNext(false);
  };

  if (error) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="text-sm text-slate-600">
          The investigation record could not be loaded.
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="rounded-lg bg-slate-800 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-slate-900"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        <ResolutionSummary rootCause={rootCause} fix={fix} />

        {/* Next action: the single most useful thing for the party who is
            waiting, because it says whether they are waiting at all. */}
        <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3">
          <p className="text-2xs font-semibold uppercase tracking-wide text-blue-700">
            Next action
          </p>

          {editingNext ? (
            <div className="mt-1.5 space-y-2">
              <textarea
                value={nextDraft}
                onChange={(e) => setNextDraft(e.target.value)}
                rows={2}
                placeholder="What happens next, in one line"
                className="w-full rounded-lg border border-blue-300 px-2.5 py-1.5 text-sm text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <div className="flex flex-wrap items-center gap-2">
                <label className="text-2xs font-semibold uppercase tracking-wide text-blue-700">
                  Owner
                </label>
                <select
                  value={ownerDraft}
                  onChange={(e) => setOwnerDraft(e.target.value)}
                  className="rounded-lg border border-blue-300 bg-white px-2 py-1 text-sm text-slate-800 focus:border-blue-500 focus:outline-none"
                >
                  <option value="expert">Expert</option>
                  <option value="client">Customer</option>
                </select>
                <div className="ml-auto flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setNextDraft(nextAction || "");
                      setOwnerDraft(nextActionOwner || "expert");
                      setEditingNext(false);
                    }}
                    className="rounded-lg px-2.5 py-1 text-sm font-medium text-slate-600 transition hover:bg-white"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={saveNext}
                    className="rounded-lg bg-blue-600 px-2.5 py-1 text-sm font-semibold text-white transition hover:bg-blue-700"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-1 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm text-slate-800">
                  {nextAction || "No next action set yet."}
                </p>
                {nextAction && nextActionOwner && (
                  <p className="mt-0.5 text-2xs text-blue-700">
                    Waiting on{" "}
                    {nextActionOwner === "client" ? "the customer" : "the expert"}
                  </p>
                )}
              </div>
              {isExpert && (
                <button
                  type="button"
                  onClick={() => setEditingNext(true)}
                  className="flex-shrink-0 rounded-lg px-2 py-1 text-2xs font-semibold uppercase tracking-wide text-blue-700 transition hover:bg-white"
                >
                  {nextAction ? "Edit" : "Set"}
                </button>
              )}
            </div>
          )}
        </div>

        {findings.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-500">
            {isExpert
              ? "No findings yet. Record what you think is happening, what you try, and what you observe — this becomes the customer's report."
              : "The expert has not recorded any findings yet. Their reasoning will appear here as they work."}
          </p>
        ) : (
          <ul className="space-y-2">
            {findings.map((finding) => (
              <FindingRow
                key={finding.id}
                finding={finding}
                filesById={filesById}
                canManage={isExpert}
                onSetStatus={onSetFindingStatus}
                onDelete={onDeleteFinding}
              />
            ))}
          </ul>
        )}
      </div>

      {/* Composer */}
      <div className="flex-shrink-0 border-t border-slate-200 bg-slate-50 px-4 py-3">
        <div className="mb-2 flex flex-wrap gap-1">
          {kinds.map((id) => {
            const definition = getFindingKind(id);
            const active = kind === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setKind(id)}
                aria-pressed={active}
                className={`rounded-lg px-2.5 py-1 text-2xs font-semibold uppercase tracking-wide transition ${
                  active
                    ? "bg-slate-800 text-white"
                    : "text-slate-600 hover:bg-white"
                }`}
              >
                {definition.shortLabel}
              </button>
            );
          })}
        </div>

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleAdd();
          }}
          rows={2}
          placeholder={getFindingKind(kind).placeholder}
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 transition focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />

        {files.length > 0 && (
          <div className="mt-2">
            <p className="mb-1 text-2xs font-semibold uppercase tracking-wide text-slate-400">
              Link evidence
            </p>
            <div className="flex flex-wrap gap-1">
              {files.slice(0, 8).map((file) => {
                const selected = selectedEvidence.includes(file.id);
                return (
                  <button
                    key={file.id}
                    type="button"
                    onClick={() => toggleEvidence(file.id)}
                    aria-pressed={selected}
                    className={`inline-flex max-w-full items-center gap-1 rounded border px-1.5 py-0.5 text-2xs transition ${
                      selected
                        ? "border-blue-300 bg-blue-50 text-blue-700"
                        : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                    }`}
                  >
                    <Paperclip className="h-3 w-3 flex-shrink-0" />
                    <span className="max-w-xs truncate">{file.filename}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-2 flex items-center justify-between gap-2">
          <p className="text-2xs text-slate-400">
            {navigator.platform?.includes("Mac") ? "Cmd" : "Ctrl"} + Enter to add
          </p>
          <button
            type="button"
            onClick={handleAdd}
            disabled={!text.trim() || saving}
            className="rounded-lg bg-slate-800 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-slate-900 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {saving ? "Adding…" : `Add ${getFindingKind(kind).shortLabel}`}
          </button>
        </div>
      </div>
    </div>
  );
}
